import { useEffect, useRef, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./lib/supabase";
import { emitToast, ToastBridge, ToastProvider } from "./components/Toast";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { AppShell } from "./components/AppShell";
import { isFull } from "./lib/auth";
import { Login } from "./pages/Login";
import { Board } from "./pages/Board";
import { NovoContrato } from "./pages/NovoContrato";
import { Handoffs } from "./pages/Handoffs";
import { ClienteDetalhe } from "./pages/ClienteDetalhe";

export default function App() {
  return (
    <ToastProvider>
      <ToastBridge />
      <AppInner />
    </ToastProvider>
  );
}

function AppInner() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const bootRetried = useRef(false);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase.auth.getSession();
      if (error || !data.session) {
        setSession(null);
        setLoading(false);
        return;
      }
      // Valida a sessão (pode estar stale no localStorage)
      const { error: userErr } = await supabase.auth.getUser();
      if (userErr && !bootRetried.current) {
        bootRetried.current = true;
        const { error: refreshErr } = await supabase.auth.refreshSession();
        if (refreshErr) {
          await supabase.auth.signOut({ scope: "global" });
          emitToast("warning", "Sessão expirou. Faça login novamente.");
          setSession(null);
          setLoading(false);
          return;
        }
        const { data: data2 } = await supabase.auth.getSession();
        setSession(data2.session);
      } else {
        setSession(data.session);
      }
      setLoading(false);
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((_evt, s) => {
      setSession(s);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  if (loading) {
    return <div className="splash">Carregando…</div>;
  }

  if (!session) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <ErrorBoundary>
      <AppShell session={session}>
        <Routes>
          <Route path="/" element={<Board session={session} />} />
          <Route
            path="/contratos/novo"
            element={isFull(session) ? <NovoContrato session={session} /> : <Navigate to="/" replace />}
          />
          <Route path="/handoffs" element={<Handoffs session={session} />} />
          <Route path="/clientes/:id" element={<ClienteDetalhe session={session} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppShell>
    </ErrorBoundary>
  );
}
